import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Database, Trash2 } from 'lucide-react';
import { getGlobalCacheStats, clearAllCaches } from '../../../services/apiClient';
import { Card, CardContent, CardFooter } from '../../../components/ui/card';
import ConfirmAction from './ConfirmAction';

/**
 * Global "Cache" view of the tenant admin console — stats aggregated across
 * the shared cache store and every tenant sub-store, plus a single
 * "clear everything" action. Thin UI over the same
 * APICache.get_stats()/clear_persistent_cache() the pre-existing (non-
 * admin-gated) /api/cache endpoints already use — this panel calls the new,
 * admin-gated /admin/tenants/cache/* endpoints instead.
 */
const GlobalCachePanel = () => {
  const { t } = useTranslation('admin');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      setStats(await getGlobalCacheStats());
    } catch (e) {
      setErr(t('errors.loadGlobalCache', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleClearAll = async () => {
    setClearing(true);
    setErr(null);
    try {
      await clearAllCaches();
      await load();
    } catch (e) {
      setErr(t('errors.clearAllCaches', { detail: e.message }));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="max-w-md">
      <h2 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-1.5">
        <Database className="w-4 h-4" /> {t('globalCache.title')}
      </h2>
      {err && <div className="mb-3 text-sm text-error-600">{err}</div>}
      {loading && !stats ? (
        <Loader className="w-4 h-4 animate-spin text-gray-400" />
      ) : (
        <Card>
          <CardContent className="p-4 text-sm text-gray-600 space-y-1.5">
            <div>{t('globalCache.cacheSize', { count: stats?.cache_size ?? 0 })}</div>
            <div>{t('globalCache.hitRate', { rate: stats?.hit_rate ?? 0 })}</div>
            <div>{t('globalCache.hitMiss', { hits: stats?.hit_count ?? 0, misses: stats?.miss_count ?? 0 })}</div>
          </CardContent>
          <CardFooter className="p-4 pt-0">
            <ConfirmAction
              label={t('globalCache.clearAll')}
              confirmLabel={t('overview.confirm')}
              cancelLabel={t('overview.cancel')}
              icon={Trash2}
              variant="destructive"
              pending={clearing}
              onConfirm={handleClearAll}
            />
          </CardFooter>
        </Card>
      )}
    </div>
  );
};

export default GlobalCachePanel;
